import React from "react";
import { IconPlayerStop, IconMaximize, IconMinimize } from "@tabler/icons-react";
import TalkingAvatar from "./TalkingAvatar";
import { ThemeConfig } from "../hooks/useVoiceWidget";
import { getStrings, Language } from "../i18n/strings";
import { NEUTRAL_PALETTE } from "../utils/neutralPalette";
import {
  HeyAiCloseIcon,
  HeyAiMicrophoneIcon,
} from "./HeyAiIcons";

interface ChatHeaderProps {
  widgetName?: string | null;
  avatarUrl?: string;
  isRecording: boolean;
  isAgentSpeaking: boolean;
  isConnected?: boolean;
  mode?: "chat" | "voice" | "combined";
  uiVariant?: "legacy" | "split-switch";
  showStopButton: boolean;
  buttonState?: "idle" | "connecting" | "recording" | "disabled";
  theme?: Partial<ThemeConfig>;
  language?: Language;
  onVoiceButtonClick?: () => void;
  onStopConversation?: () => void;
  onClose?: () => void;
  /** Split-switch only: current enlarged state and a toggle for it. */
  isEnlarged?: boolean;
  onToggleEnlarge?: () => void;
}

const LegacyPillButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant: "danger" | "primary" | "muted";
  disabled?: boolean;
}> = ({ icon, label, onClick, variant, disabled }) => {
  const colors = {
    danger: { bg: "#ef4444" },
    primary: { bg: "#4f46e5" },
    muted: { bg: "#6b7280" },
  };
  const c = colors[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 10px",
        borderRadius: "14px",
        border: "none",
        background: c.bg,
        color: "white",
        fontSize: "12px",
        fontWeight: "500",
        fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "background 0.15s ease",
        lineHeight: "1",
        whiteSpace: "nowrap",
      }}
      title={label}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};

const IconPillButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant: "danger" | "ghost";
  disabled?: boolean;
}> = ({ icon, label, onClick, variant, disabled }) => {
  const colors = {
    danger: {
      bg: NEUTRAL_PALETTE.pearl,
      fg: NEUTRAL_PALETTE.anthracite,
      border: NEUTRAL_PALETTE.mist,
    },
    ghost: {
      bg: NEUTRAL_PALETTE.pearl,
      fg: NEUTRAL_PALETTE.anthracite,
      border: NEUTRAL_PALETTE.mist,
    },
  };
  const c = colors[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "7px 10px",
        borderRadius: "999px",
        border: `1px solid ${c.border}`,
        background: c.bg,
        color: c.fg,
        fontSize: "12px",
        fontWeight: "700",
        fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "background 0.15s ease, border-color 0.15s ease",
        lineHeight: "1",
        whiteSpace: "nowrap",
      }}
      title={label}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};

const ChatHeader: React.FC<ChatHeaderProps> = ({
  widgetName,
  avatarUrl,
  isRecording,
  isAgentSpeaking,
  isConnected = false,
  mode = "chat",
  uiVariant = "legacy",
  showStopButton,
  buttonState = "idle",
  language,
  onVoiceButtonClick,
  onStopConversation,
  onClose,
  isEnlarged = false,
  onToggleEnlarge,
}) => {
  const t = getStrings(language);
  const useSplitSwitch = mode === "combined" && uiVariant === "split-switch";
  const resizeExpandLabel = language === "en" ? "Enlarge" : "Vergrößern";
  const resizeRestoreLabel = language === "en" ? "Restore size" : "Verkleinern";
  const voiceButtonLabel = useSplitSwitch
    ? buttonState === "connecting" ? t.statusConnecting : t.voiceMode
    : buttonState === "connecting"
      ? t.statusConnecting
      : isRecording
        ? t.buttonVoiceActive
        : isConnected
          ? t.buttonSwitchToVoice
          : t.buttonVoice;
  const closeButton = onClose ? (
    <button
      onClick={onClose}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        border: "none",
        background: "transparent",
        color: NEUTRAL_PALETTE.slate,
        cursor: "pointer",
        transition: "color 0.15s ease",
        padding: 0,
        flexShrink: 0,
      }}
      title={t.buttonClose}
    >
      <HeyAiCloseIcon size={16} />
    </button>
  ) : null;

  if (useSplitSwitch) {
    return (
      <div
        data-testid="heyai-panel-header"
        className="heyai-panel-section heyai-panel-section--header"
        style={{
          padding: "13px 16px 11px",
          background: "rgba(255, 255, 255, 0.26)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.36)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: "14px",
              color: NEUTRAL_PALETTE.anthracite,
              lineHeight: "1.2",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {widgetName || "HeyAI"}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            flexShrink: 0,
          }}
        >
          {showStopButton && onStopConversation && (
            <IconPillButton
              icon={<IconPlayerStop size={13} />}
              label={t.buttonStop}
              onClick={onStopConversation}
              variant="danger"
            />
          )}
          {onToggleEnlarge && (
            <button
              type="button"
              data-testid="heyai-panel-resize"
              onClick={onToggleEnlarge}
              aria-label={isEnlarged ? resizeRestoreLabel : resizeExpandLabel}
              title={isEnlarged ? resizeRestoreLabel : resizeExpandLabel}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                border: "none",
                background: "transparent",
                color: NEUTRAL_PALETTE.slate,
                cursor: "pointer",
                transition: "color 0.15s ease",
                padding: 0,
                flexShrink: 0,
              }}
            >
              {isEnlarged ? <IconMinimize size={15} /> : <IconMaximize size={15} />}
            </button>
          )}
          {closeButton}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "12px 16px",
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <TalkingAvatar
        isRecording={isRecording}
        isSpeaking={isAgentSpeaking}
        size={36}
        avatarUrl={avatarUrl}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: "600",
            fontSize: "14px",
            color: "#1e293b",
            lineHeight: "1.2",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {widgetName || t.fallbackName}
        </div>
        <div style={{ fontSize: "11px", color: "#64748b" }}>
          {isRecording ? t.statusListening : t.statusOnline}
        </div>
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
        {/* Stop Button */}
        {showStopButton && onStopConversation && (
          <LegacyPillButton
            icon={<IconPlayerStop size={13} />}
            label={t.buttonStop}
            onClick={onStopConversation}
            variant="danger"
          />
        )}

        {mode === "combined" && onVoiceButtonClick && (
          <LegacyPillButton
            icon={<HeyAiMicrophoneIcon size={13} />}
            label={voiceButtonLabel}
            onClick={onVoiceButtonClick}
            variant={isRecording ? "muted" : "primary"}
            disabled={buttonState === "disabled" || buttonState === "connecting" || isRecording}
          />
        )}

        {/* Close / collapse button */}
        {closeButton}
      </div>
    </div>
  );
};

export default ChatHeader;
